import { useId } from 'react';

/**
 * A labelled form control.
 *
 * One implementation. The old codebase had two plus inline copies, and none of them
 * associated the label with the input: labels were styled divs with no `htmlFor`, so
 * clicking one did not focus its field and a screen reader never announced it.
 *
 * The id is generated with `useId` rather than passed in, so two Fields with the same
 * label on one screen cannot collide.
 *
 * Errors carry `role="alert"` and are wired through `aria-describedby`, so they are
 * announced rather than only shown in red.
 *
 * Audit references prevented here: shared.jsx #11, auth.jsx #16, auth.jsx #14.
 *
 * @param {object} props
 * @param {string} props.label
 * @param {(props: {id: string, describedBy: string|undefined, invalid: boolean}) => import('react').ReactNode} props.children
 *   Render function receiving the ids to attach. A render prop rather than cloning a
 *   child, because cloning silently overrides whatever the caller set.
 * @param {string} [props.hint]
 * @param {string|null} [props.error]
 * @param {boolean} [props.required]
 */
export function Field({ label, children, hint, error = null, required = false }) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
        {required ? (
          <span className="field__required" aria-hidden="true">
            {' '}
            *
          </span>
        ) : null}
      </label>

      {children({ id, describedBy: describedBy || undefined, invalid: Boolean(error) })}

      {hint ? (
        <p className="field__hint" id={hintId}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p className="field__error" id={errorId} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
